/**
 * Color System for IntelligenceBank UI Primitives
 * 
 * Colors sourced from the ib-ui library design system.
 * This file provides a comprehensive color palette compatible with the
 * IntelligenceBank main platform.
 * 
 * @see /Users/charlyvanni/Workspace/IntelligenceBank/ib/dev_override/ib-ui/src/Atoms/Colors
 */

// =============================================================================
// Grey Scale
// =============================================================================

/**
 * Grey scale colors - Used for backgrounds, borders, and text
 * Ranges from lightest (grey100) to darkest (grey900)
 */
export const greyScale = {
  /** Lightest grey - subtle backgrounds */
  grey100: '#F7F7F7',
  /** Light grey - card backgrounds, dividers */
  grey150: '#E5E5E5',
  /** Light grey - borders, disabled backgrounds */
  grey200: '#D9D9D9',
  /** Medium light grey - borders */
  grey300: '#BFBFBF',
  /** Medium grey - disabled text */
  grey400: '#A1A1A1',
  /** Medium grey - secondary text, icons */
  grey500: '#737373',
  /** Medium dark grey - secondary text */
  grey600: '#595959',
  /** Dark grey - primary text alternative */
  grey700: '#3F3F3F',
  /** Dark grey - headers, primary backgrounds (PRIMARY_DEFAULT) */
  grey800: '#262626',
  /** Darkest grey - near black */
  grey900: '#191919',
} as const;

// =============================================================================
// Semantic Colors - Green (Success)
// =============================================================================

/**
 * Green colors - Used for success states, confirmations, positive actions
 */
export const greenColors = {
  /** Lightest green - success backgrounds */
  greenLighter: '#E5F6E8',
  /** Light green - success highlights */
  greenLight: '#54C164',
  /** Base green - primary success color */
  greenBase: '#2B8048',
  /** Dark green - success hover states */
  greenDark: '#046211',
  /** Darkest green - success active states */
  greenDarker: '#033707',
} as const;

// =============================================================================
// Semantic Colors - Red (Error/Destructive)
// =============================================================================

/**
 * Red colors - Used for errors, warnings, destructive actions
 */
export const redColors = {
  /** Very light red - error backgrounds (alternative) */
  redLighter2: '#FCECE7',
  /** Lightest red - error backgrounds */
  redLighter: '#FFE3E5',
  /** Light red - error highlights */
  redLight: '#F16C71',
  /** Base red - primary error color */
  redBase: '#CA212B',
  /** Dark red - error hover states */
  redDark: '#97090E',
  /** Darkest red - error active states */
  redDarker: '#5F0508',
} as const;

// =============================================================================
// Semantic Colors - Blue (Info/Links)
// =============================================================================

/**
 * Blue colors - Used for informational states, links, selected items
 */
export const blueColors = {
  /** Very light blue - info backgrounds (alternative) */
  blueLighter2: '#E0EBFD',
  /** Lightest blue - info backgrounds */
  blueLighter: '#DDE9FC',
  /** Light blue - info highlights */
  blueLight: '#5493F0',
  /** Base blue - primary info color */
  blueBase: '#0053CD',
  /** Dark blue - info hover states */
  blueDark: '#003B91',
  /** Darkest blue - info active states */
  blueDarker: '#012355',
  /** Link blue - standard link color */
  blueLink: '#0066CC',
} as const;

// =============================================================================
// Semantic Colors - Orange (Warning)
// =============================================================================

/**
 * Orange colors - Used for warning states, pending status, attention
 */
export const orangeColors = {
  /** Very light orange - warning backgrounds (alternative) */
  orangeLighter2: '#FFEFC6',
  /** Lightest orange - warning backgrounds */
  orangeLighter: '#FFEEDD',
  /** Light orange - warning highlights */
  orangeLight: '#FFBA7C',
  /** Base orange - primary warning color */
  orangeBase: '#F86C05',
  /** Dark orange - warning hover states */
  orangeDark: '#B3520C',
  /** Darkest orange - warning active states */
  orangeDarker: '#6D3106',
} as const;

// =============================================================================
// Legacy/Utility Colors
// =============================================================================

/**
 * Legacy text colors - For backward compatibility with ib-ui
 */
export const textColors = {
  /** Primary text color */
  textColor: '#4a4a4a',
  /** Faded text color */
  textColorFade: 'rgba(74,74,74, 0.5)',
  /** Instruction/help text color */
  instructionColor: '#737474',
  /** Help text color (same as lighterGrey) */
  helpTextColor: '#9b9b9b',
  /** White text for dark backgrounds */
  whiteTextColor: '#ffffff',
  /** Highlight text color (white) */
  highlightTextColor: '#ffffff',
} as const;

/**
 * Legacy grey colors - For backward compatibility with ib-ui
 */
export const legacyGreyColors = {
  /** Light grey (same as grey500) */
  lightGrey: '#727274',
  /** Lighter grey */
  lighterGrey: '#9b9b9b',
  /** Lightest grey */
  lightestGrey: '#f4f4f4',
} as const;

/**
 * Border colors
 */
export const borderColors = {
  /** Standard border grey */
  borderGrey: '#d0d0d0',
  /** Lighter border grey */
  borderLighterGrey: '#e0e0e0',
  /** Border shadow color */
  borderShadow: '#d8d8d8',
} as const;

/**
 * Icon colors
 */
export const iconColors = {
  /** Standard icon grey */
  iconGrey: '#a3a3a3',
  /** Disabled icon grey */
  iconGreyDisabled: 'rgba(163,163,163, 0.5)',
  /** Icon background overlay */
  iconBackground: 'rgba(0, 0, 0, 0.10)',
} as const;

/**
 * Background colors
 */
export const backgroundColors = {
  /** Processing/loading background overlay */
  backgroundProcessingColor: 'rgba(163, 163, 163, .3)',
  /** Standard white background */
  backgroundWhiteColor: '#ffffff',
  /** Row/stripe background grey */
  rowGrey: '#f9f9f9',
} as const;

/**
 * Base colors
 */
export const baseColors = {
  /** White */
  whiteColor: '#ffffff',
  /** Black */
  blackColor: '#000000',
  /** Yellow (notification, highlight) */
  yellow: '#FFEFC6',
} as const;

// =============================================================================
// Status/Feedback Colors
// =============================================================================

/**
 * Status colors for messaging, alerts, notifications
 */
export const statusColors = {
  /** Success status (green) */
  successColor: '#21B63C',
  /** Error status (red) */
  errorColor: '#d31818',
  /** Warning status (orange) */
  warningColor: '#f88b07',
  /** Info status (orange) */
  infoColor: '#f88b07',
  /** Notice status (orange) */
  noticeColor: '#FFA500',
} as const;

/**
 * Snackbar/Toast background colors
 */
export const snackbarColors = {
  /** Snackbar error background */
  snackbarErrorBackground: '#D01C23',
  /** Snackbar success background */
  snackbarSuccessBackground: '#108c27',
  /** Snackbar warning background */
  snackbarWarningBackground: '#F97E24',
} as const;

/**
 * Progress bar colors
 */
export const progressColors = {
  /** Completed progress */
  progressBarCompletedColor: '#108C27',
  /** Error progress */
  progressBarErrorColor: '#D01C23',
} as const;

// =============================================================================
// Transparency Utilities
// =============================================================================

/**
 * Transparent black overlays - for various UI states
 */
export const transparentBlack = {
  /** 50% black overlay */
  transparentBlack50: 'rgba(0, 0, 0, 0.50)',
  /** 20% black overlay */
  transparentBlack20: 'rgba(0, 0, 0, 0.20)',
  /** 8% black overlay - subtle hover */
  transparentBlack08: 'rgba(0, 0, 0, 0.08)',
  /** 5% black overlay - very subtle */
  transparentBlack05: 'rgba(0, 0, 0, 0.05)',
} as const;

/**
 * Transparent white overlays - for dark backgrounds
 */
export const transparentWhite = {
  /** 50% white overlay */
  transparentWhite50: 'rgba(255,255,255,0.50)',
  /** 20% white overlay */
  transparentWhite20: 'rgba(255,255,255,0.20)',
  /** 5% white overlay */
  transparentWhite05: 'rgba(255,255,255,0.05)',
} as const;

// =============================================================================
// Dynamic Tenant Colors (Fallback Values)
// =============================================================================

/**
 * Default/fallback values for the 6 dynamic tenant colors.
 *
 * These values are used when constructing the theme and no tenant-specific
 * colors are provided from the login API response.
 *
 * IMPORTANT: Components should NOT import these directly.
 * Instead, use:
 *   - theme.colors.highlight (via useTheme())
 *   - var(--color-highlight) (in CSS)
 *
 * The theme handles the "default vs dynamic" resolution automatically.
 *
 * Login Response Field    →    Theme Property           →    CSS Variable
 * ─────────────────────────────────────────────────────────────────────────
 * colourPrimary           →    colors.primary           →    --color-primary
 * colourPrimaryText       →    colors.primaryText       →    --color-primary-text
 * colourSecondary         →    colors.secondary         →    --color-secondary
 * colourSecondaryText     →    colors.secondaryText     →    --color-secondary-text
 * colourHighlight         →    colors.highlight         →    --color-highlight
 * colourHighlightText     →    colors.highlightText     →    --color-highlight-text
 */
export const defaultPaletteColors = {
  /** Primary color fallback - white background */
  PRIMARY_DEFAULT: '#ffffff',
  /** Primary text color fallback - black on primary backgrounds */
  PRIMARY_TEXT_DEFAULT: '#000000',
  /** Secondary color fallback - orange accent */
  SECONDARY_DEFAULT: '#F68D32',
  /** Secondary text color fallback - white on secondary backgrounds */
  SECONDARY_TEXT_DEFAULT: '#ffffff',
  /** Highlight/accent color fallback - brand orange */
  HIGHLIGHT_DEFAULT: '#F4770B',
  /** Highlight text color fallback - white on highlight backgrounds */
  HIGHLIGHT_TEXT_DEFAULT: '#ffffff',
} as const;

/**
 * User default colors
 */
export const userDefaultColors = {
  /** Default text color for user-generated content */
  userDefaultTextColor: '#000000',
  /** Default background color for user-generated content */
  userDefaultBackgroundColor: '#ffffff',
} as const;

// =============================================================================
// Slider/Form Colors
// =============================================================================

/**
 * Form element colors
 */
export const formColors = {
  /** Slider track/thumb color */
  sliderGrey: '#bdbdbd',
} as const;

// =============================================================================
// Shadows (from ib-ui)
// =============================================================================

/**
 * Shadow values from ib-ui design system
 */
export const ibUiShadows = {
  /** Level 1 shadow - subtle elevation */
  shadow100: '0px 1px 3px rgba(0, 0, 0, 0.25)',
  /** Level 2 shadow - cards, dropdowns */
  shadow200: '0px 2px 5px rgba(0, 0, 0, 0.25)',
  /** Level 3 shadow - modals, popovers */
  shadow300: '0px 3px 12px rgba(0, 0, 0, 0.25)',
  /** Footer shadow - upward facing */
  shadowFooter: '0px -1px 2px rgba(0, 0, 0, 0.25)',
  /** Selected item shadow - emphasized */
  shadowSelected: '0px 2px 5px rgba(0, 0, 0, 0.50)',
} as const;

// =============================================================================
// Border Radius (from ib-ui)
// =============================================================================

/**
 * Border radius values from ib-ui design system
 */
export const borderRadius = {
  /** Small radius - buttons, inputs (4px) */
  borderRadiusSmall: '4px',
  /** Medium radius - cards (8px) */
  borderRadiusMedium: '8px',
  /** Large radius - modals, containers (16px) */
  borderRadiusLarge: '16px',
} as const;

// =============================================================================
// Combined Exports
// =============================================================================

/**
 * All grey colors combined (scale + legacy)
 */
export const allGreyColors = {
  ...greyScale,
  ...legacyGreyColors,
} as const;

/**
 * All semantic colors combined (ib-ui version)
 */
export const ibUiSemanticColors = {
  green: greenColors,
  red: redColors,
  blue: blueColors,
  orange: orangeColors,
} as const;

/**
 * Complete color palette - all colors from ib-ui
 */
export const ibUiColors = {
  // Scales
  grey: greyScale,
  
  // Semantic
  green: greenColors,
  red: redColors,
  blue: blueColors,
  orange: orangeColors,
  
  // Status
  status: statusColors,
  snackbar: snackbarColors,
  progress: progressColors,
  
  // UI Elements
  text: textColors,
  border: borderColors,
  icon: iconColors,
  background: backgroundColors,
  form: formColors,
  
  // Base
  base: baseColors,
  
  // Transparency
  transparentBlack,
  transparentWhite,
  
  // Tenant fallback values (actual values come from theme context)
  highlight: defaultPaletteColors.HIGHLIGHT_DEFAULT,
  palette: defaultPaletteColors,
  user: userDefaultColors,
} as const;

// =============================================================================
// Type Exports
// =============================================================================

export type GreyScale = typeof greyScale;
export type GreenColors = typeof greenColors;
export type RedColors = typeof redColors;
export type BlueColors = typeof blueColors;
export type OrangeColors = typeof orangeColors;
export type TextColors = typeof textColors;
export type BorderColors = typeof borderColors;
export type IconColors = typeof iconColors;
export type BackgroundColors = typeof backgroundColors;
export type BaseColors = typeof baseColors;
export type StatusColors = typeof statusColors;
export type SnackbarColors = typeof snackbarColors;
export type ProgressColors = typeof progressColors;
export type TransparentBlack = typeof transparentBlack;
export type TransparentWhite = typeof transparentWhite;
export type DefaultPaletteColors = typeof defaultPaletteColors;
export type IbUiShadowsType = typeof ibUiShadows;
export type BorderRadius = typeof borderRadius;
export type IbUiColors = typeof ibUiColors;

export default ibUiColors;
